import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function StaffProviders() {
  const { user } = useAuth();
  const isDirector = user.roles.includes("care_director");
  const { data: providers = [] } = useQuery({ queryKey: ["providers"], queryFn: async () => (await api.get("/providers")).data });
  return (
    <div data-testid="staff-providers">
      <h1 className="text-4xl">Counselors & Credentials</h1>
      <p className="mt-3 max-w-[65ch] text-ink-500">Every person with the Counselor or Supervisor role. Provider type, license, supervision, background check, and mandated-reporter training decide which cases a counselor may be assigned. Give someone the Counselor role under People & Roles first.</p>
      <ul className="mt-8 divide-y divide-sage-400/50 border-y border-sage-400/50" data-testid="providers-list">
        {providers.map((p) => (
          <li key={p.user_id} className="flex flex-wrap items-start justify-between gap-4 py-5" data-testid={`provider-${p.user_id}`}>
            <div className="min-w-0">
              <p className="font-serif text-xl font-semibold">{p.display_name || p.name} <span className="font-sans text-base font-normal text-ink-500">· {p.has_profile ? p.provider_type_label : "no profile yet"}{p.title ? ` · ${p.title}` : ""}</span></p>
              <p className="mt-1 text-sm text-ink-500">{p.email}{p.has_profile ? ` · ${p.active_cases}/${p.max_active_cases} active cases · ${p.campuses?.length ? p.campuses.join(", ") : "no campuses"}` : ""}</p>
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                {p.has_profile && <span className={`border px-2 py-0.5 ${p.in_good_standing ? "border-olive-900" : "border-terracotta-600 text-terracotta-600"}`}>{p.in_good_standing ? "In good standing" : "Needs attention"}</span>}
                {p.cleared_for_minors && <span className="border border-olive-900 px-2 py-0.5">Cleared for minors</span>}
                {p.cleared_for_couples && <span className="border border-olive-900 px-2 py-0.5">Couples / family</span>}
                {p.public_profile && <span className="border border-sage-400 px-2 py-0.5">Public profile</span>}
                {p.status === "inactive" && <span className="border border-terracotta-600 px-2 py-0.5 text-terracotta-600">Inactive</span>}
              </div>
              {(p.blockers?.length > 0 || p.warnings?.length > 0) && (
                <ul className="mt-2 space-y-0.5 text-sm" data-testid={`provider-flags-${p.user_id}`}>
                  {p.blockers.map((b) => <li key={b} className="text-terracotta-600">{b}</li>)}
                  {p.warnings.map((w) => <li key={w} className="text-ink-500">{w}</li>)}
                </ul>
              )}
            </div>
            {isDirector && <Link to={`/staff/providers/${p.user_id}`} className="btn-secondary min-h-[40px]" data-testid={`provider-edit-${p.user_id}`}>{p.has_profile ? "Edit Credentials" : "Create Profile"}</Link>}
          </li>
        ))}
        {providers.length === 0 && <li className="py-6 text-ink-500">No one has the Counselor role yet.</li>}
      </ul>
    </div>
  );
}
